import styled, { createGlobalStyle, keyframes } from "styled-components";

import { InfoContainer } from "../styles";

import { SecondaryButton } from "components/Button";

export interface ICarousel {
  isActive: boolean;
  timer: number;
  paused: boolean;
}

export interface IDisplayImage {
  background?: string;
}

export interface IGradient {
  background?: string;
}

// Fade animation
export const fade = keyframes`
  from {
    opacity: 0.4;
  }

  to {
    opacity:1;
  }
`;

export const CarouselContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CarouselSlides = styled.div`
  position: relative;
  width: 327px;
  height: 400px;

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    width: 696px;
    height: 500px;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    width: 1156px;
    height: 450px;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("xxl")} {
    width: 1572px;
    height: 580px;
  }
`;

export const CarouselDefaultContainer = styled.div<IDisplayImage>`
  position: relative;
  background-repeat: no-repeat;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0px 15px 50px 0px rgba(230, 0, 0, 0.15);
  background: ${({ theme }) =>
    `linear-gradient(135.84deg, ${theme.customTheme.palette.red.bannerE4} 39.47%, ${theme.customTheme.palette.red.banner72} 93.5%)`};

  video,
  img {
    z-index: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;

    // Move to compositor layer to improve sharpness since object-fit: cover can cause some blur
    will-change: transform;
    transform: translate3d(0, 0, 1px);
  }
`;

export const Slide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  & > * {
    display: relative;
    width: 100%;
    height: 100%;
  }
`;

export const CarouselCard = styled.div`
  position: relative;
  background-repeat: no-repeat;
  border-radius: 20px;
  background: ${({ theme }) =>
    `linear-gradient(135.84deg, ${theme.customTheme.palette.red.bannerE4} 39.47%, ${theme.customTheme.palette.red.banner72} 93.5%)`};
  box-shadow: 0px 15px 50px 0px rgba(230, 0, 0, 0.15);
  background-position-x: 50%;
  background-position-y: 55%;
  background-size: 250%;
  text-align: center;
  display: flex;
  flex-direction: column;
  background-clip: padding-box;

  .border-radius-bleed-fix & {
    border-radius: 25px 25px 20px 20px;

    ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
      border-radius: 20px 25px 25px 20px;
    }
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    text-align: left;
    flex-direction: row-reverse;
    background-position-x: 45%;
    background-position-y: 55%;
    background-size: 140%;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    background-position-x: 5%;
    background-position-y: 58%;
    background-size: 150%;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("xxl")} {
    background-position-x: 10%;
    background-position-y: 58%;
    background-size: 140%;
  }
`;

export const BackgroundGradient = styled.div<IGradient>`
  background: linear-gradient(64.53deg, rgba(0, 0, 0, 0) 73.7%, rgba(0, 0, 0, 0.6) 91.84%);
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border-radius: 20px 20px 0px 0px;

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    background: linear-gradient(60deg, rgba(0, 0, 0, 0) 75.39%, rgba(0, 0, 0, 0.6) 95.46%);
  }
  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    background: linear-gradient(44.21deg, rgba(0, 0, 0, 0) 72.75%, rgba(0, 0, 0, 0.6) 94.96%);
  }
`;

export const ImageDisplay = styled.div<IDisplayImage>`
  flex: 1 1 50%;
  position: relative;
  height: 100%;
  background-clip: padding-box;
  background-color: ${({ theme }) => theme.customTheme.palette.black.dark};
  border-radius: 20px 20px 0px 0px;

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    border-radius: 0px 20px 20px 0px;
  }

  video,
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    border-radius: 20px 20px 0px 0px;
    background: linear-gradient(64.53deg, rgba(0, 0, 0, 0) 73.7%, rgba(0, 0, 0, 0.6) 91.84%);
    background-clip: padding-box;
    // Move to compositor layer to improve sharpness
    will-change: transform;
    transform: translate3d(0, 0, 1px);

    ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
      border-radius: 0px 20px 20px 0px;
      transform: translate3d(0, 0, 1px);
      background: linear-gradient(60deg, rgba(0, 0, 0, 0) 75.39%, rgba(0, 0, 0, 0.6) 95.46%);
    }

    ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
      background: linear-gradient(44.21deg, rgba(0, 0, 0, 0) 72.75%, rgba(0, 0, 0, 0.6) 94.96%);
    }
  }

  video {
    object-fit: contain;
  }
`;

export const PartnerLogo = styled.div<IDisplayImage>`
  position: absolute;
  width: 80px;
  height: 40px;
  top: 12px;
  right: 12px;
  background-image: url(${({ background }) => background});
  background-size: 80%;
  background-position: center;
  background-repeat: no-repeat;

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    top: 20px;
    right: 20px;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    top: 28px;
    right: 28px;
    width: 100px;
    height: 50px;
  }

  img {
    width: 100%;
    object-fit: cover;
    object-position: bottom;
  }
`;

export const DescriptionContainer = styled.div`
  flex: 1 1 50%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 0px 0px 20px 20px;

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    align-items: flex-start;
    padding: 66px;
    border-radius: 20px 0px 0px 20px;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    padding: 94px;
  }
`;

export const Title = styled.div`
  font-family: ThemeFontRegular;
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  color: ${({ theme }) => theme.customTheme.palette.white.standard};
  text-align: center;
  margin-bottom: 12px;

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    font-size: 32px;
    font-weight: 700;
    line-height: 36px;
    text-align: left;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    font-size: 32px;
    font-weight: 700;
    line-height: 40px;
    margin-bottom: 16px;
  }
`;

export const Description = styled.div`
  font-family: ThemeFontRegular;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme }) => theme.customTheme.palette.white.standard};
  text-align: center;
  margin-bottom: 24px;

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    margin-bottom: 32px;
    text-align: left;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    font-size: 20px;
    font-weight: 400;
    line-height: 24px;
    margin-bottom: 40px;
  }
`;

export const DotContainer = styled.div`
  text-align: center;
  padding: 8px;
`;

export const Dot = styled.span<ICarousel>`
  max-width: 1200px;
  height: 4px;
  width: ${(props) => (props.isActive ? "80px" : "4px")};
  margin: 0 2px;
  background-color: ${({ theme }) => theme.customTheme.palette.grey.standard};
  border-radius: 16px;
  display: inline-block;
  position: relative; /* Position relative for absolute positioning of loading animation */
  overflow: hidden; /* Hide overflow for loading animation */

  /* Loading animation */
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: ${(props) => (props.isActive ? "100%" : "0")}; /* Set width to 100% when active, 0 otherwise */
    height: 100%;
    border-radius: 9999px;
    background-color: ${(props) => (props.isActive ? props.theme.customTheme.palette.black.black25 : "#bbb")};
    animation: ${(props) =>
      props.isActive && `loadingAnimation ${props.timer}s linear`}; /* Start animation when active */
    animation-play-state: ${({ paused }) => (paused ? "paused" : "running")};
  }

  /* Define the loading animation */
  @keyframes loadingAnimation {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(0%);
    }
  }
`;

export const ActionButton = styled(SecondaryButton)`
  font-family: ThemeFontBold;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0.0168em;
  height: 40px;
  border-radius: 9999px;
  padding: 12px 20px;
  gap: 8px;
  background-color: ${({ theme }) => theme.customTheme.palette.white.standard};
  color: ${({ theme }) => theme.customTheme.palette.black.standard};
  box-shadow: 0px 16px 32px rgba(0, 0, 0, 0.15);

  &:hover,
  &:focus {
    filter: brightness(100%);
  }
`;

export const InfoContainerCarousel = styled(InfoContainer)`
  position: relative;
  z-index: 1;
  padding: 32px 0px 0px 0px;

  ${({ theme }) => theme.customTheme.breakpoints.up("md")} {
    padding: 64px 118px 0px 118px;
  }

  ${({ theme }) => theme.customTheme.breakpoints.up("lg")} {
    padding: 149px 118px 0px 118px;
  }
`;

export const CarouselGlobals = createGlobalStyle`
  #carousel-displayer {
    .enter {
      transition: opacity 200ms ease-out;
    }
    .enterFrom {
      opacity: 0;
    }
    .enterTo {
      opacity: 1;
    }
    .leave {
      transition: opacity 150ms ease-in;
    }
    .leaveFrom {
      opacity: 1;
    }
    .leaveTo {
      opacity: 0;
    }
  }
`;
